/**
 * Trust & Safety Center — content configuration.
 *
 * Each page has: id, title, icon (lucide name), summary, and sections.
 * Sections are lists of { heading, body } where body can be:
 *   - a string (rendered as <p>)
 *   - an object { list: [...] } for bullet lists
 *   - an object { note: '...' } for highlighted callouts
 *
 * Rendered by pages/TrustSafety.js.
 */

export const TRUST_SAFETY_PAGES = [
  /* ---------------- 1. Terms of Use ---------------- */
  {
    id: 'terms',
    title: 'Terms of Use',
    icon: 'FileText',
    summary: 'The agreement between you and Brotherhood+ when you use the app.',
    sections: [
      { heading: 'Eligibility', body: ['You must be at least 18 years old to use Brotherhood+. By creating an account you confirm the information you provide is accurate.'] },
      { heading: 'Acceptable Use', body: ['Use Brotherhood+ for personal, non-commercial wellness purposes. Do not attempt to reverse-engineer, scrape, resell, or misuse the service, and do not upload content that is illegal, harmful, or that infringes on others.'] },
      { heading: 'User Accounts', body: ['You are responsible for keeping your account credentials secure. Notify us immediately if you suspect unauthorized access. Brotherhood+ is not liable for losses arising from failure to secure your account.'] },
      { heading: 'AI Services', body: ['Brotherhood+ includes AI-powered coaching for education and reflection. AI output may occasionally be inaccurate and is not a substitute for professional care. See the AI Coach Disclosure for full details.'] },
      { heading: 'Wellness Tools', body: ['Check-ins, journals, and assessments are designed to build self-awareness. They are informational only and do not constitute medical, psychological, or therapeutic advice.'] },
      { heading: 'Subscription Terms', body: ['Some features require a Brotherhood+ subscription. Full terms — pricing, renewal, cancellation, refunds — are described in the Subscription & Billing page.'] },
      { heading: 'Billing', body: ['Subscriptions renew automatically unless cancelled at least 24 hours before the renewal date. Applicable taxes may be added. You can manage your subscription from Profile → Subscription.'] },
      { heading: 'Intellectual Property', body: ['All Brotherhood+ content — text, illustrations, code, brand marks — is owned by Brotherhood+ or its licensors. You retain ownership of content you create, and grant Brotherhood+ a limited license to store and display it so you can use the service.'] },
      { heading: 'User Content', body: ['You are responsible for content you create in Brotherhood+ (journal entries, chats, community posts). You retain ownership. Do not post content that is illegal, harmful, sexually exploitative, or that violates another person\'s privacy.'] },
      { heading: 'Community Standards', body: ['See the Community Guidelines page. Violations may result in warnings, feature restrictions, or account termination.'] },
      { heading: 'Limitation of Liability', body: ['To the maximum extent permitted by law, Brotherhood+ and its officers, employees, and affiliates are not liable for indirect, incidental, or consequential damages arising from your use of the service.'] },
      { heading: 'Disclaimer of Warranties', body: ['Brotherhood+ is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that the service will be uninterrupted, secure, or error-free.'] },
      { heading: 'Termination', body: ['You may close your account at any time from Profile → Delete account. We may suspend or terminate accounts that violate these Terms or that pose a risk to other users.'] },
      { heading: 'Governing Law', body: ['These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict-of-laws principles. Disputes will be resolved in the state or federal courts located in that jurisdiction.'] },
      { heading: 'Contact Information', body: ['Questions about these Terms? Email brotherhoodplusapp@gmail.com.'] },
    ],
  },

  /* ---------------- 2. Privacy Policy ---------------- */
  {
    id: 'privacy',
    title: 'Privacy Policy',
    icon: 'Lock',
    summary: 'What we collect, why we collect it, and how you stay in control.',
    sections: [
      {
        heading: 'What we collect',
        body: [
          'We collect only what is needed to make Brotherhood+ work for you:',
          { list: [
            'Account information — name, email, hashed password',
            'Wellness check-ins — mood, stress, sleep, and energy inputs',
            'Journal entries — the text you write and any AI-generated reflections',
            'AI conversations — messages you exchange with the Coach and Support Coach',
            'Assessments — your answers and computed wellness snapshot',
            'Progress tracking — completions, streaks, achievements',
            'Device information — browser type and language, aggregated for reliability',
          ]},
        ],
      },
      { heading: 'Why we collect it', body: ['To personalise your Brotherhood+ experience, generate meaningful insights, secure your account, provide customer support, and improve the platform. We never sell your data and do not share it with data brokers.'] },
      { heading: 'How we protect it', body: ['Data is encrypted in transit (TLS 1.2+) and at rest. Access is restricted to authorized team members on a need-to-know basis. Third-party services we use (e.g. hosting, AI, payments) are contractually bound to protect your data.'] },
      { heading: 'How long we keep it', body: ['We retain your data for as long as your account is active. When you delete your account, we delete personal data within 30 days, retaining only anonymised aggregates and records we\'re legally required to keep (e.g. billing records).'] },
      {
        heading: 'Your rights',
        body: [
          'You have the right to access, correct, export, or delete your data at any time — no explanation required.',
          { list: [
            'Export — Profile → Download my data (instant JSON export)',
            'Delete — Profile → Delete account (permanent, immediate removal)',
            'Correct — update your name, email, and preferences from Profile',
            'Object / restrict — email brotherhoodplusapp@gmail.com for anything else',
          ]},
        ],
      },
      { heading: 'Third-party providers', body: ['We use vetted providers for hosting, AI language models, email delivery, and payments. Each is bound by data-processing agreements. A full list is available on request from brotherhoodplusapp@gmail.com.'] },
      { heading: 'Analytics', body: ['We use privacy-respecting analytics to understand which features are used, without profiling individual users. No third-party ad networks or trackers are present in Brotherhood+.'] },
      { heading: 'Cookies', body: ['See the Cookie & Analytics Policy page for the full breakdown and controls.'] },
    ],
  },

  /* ---------------- 3. AI Coach Disclosure ---------------- */
  {
    id: 'ai-disclosure',
    title: 'AI Coach Disclosure',
    icon: 'Bot',
    summary: 'What the AI Coach is — and isn\'t.',
    sections: [
      { heading: 'A companion for reflection', body: ['Brotherhood+ includes an AI-powered wellness coach designed for education, reflection, and personal growth. It\'s built to help you think out loud, notice patterns, and take small steady steps.'] },
      {
        heading: 'What the AI Coach is NOT',
        body: [
          { list: [
            'It is NOT a licensed therapist.',
            'It is NOT medical care.',
            'It is NOT psychotherapy.',
            'It does NOT diagnose conditions.',
            'It does NOT prescribe medication.',
            'It does NOT replace a licensed mental health professional.',
            'It may occasionally produce inaccurate or incomplete information.',
          ]},
        ],
      },
      { heading: 'When to seek a professional', body: ['If you\'re experiencing persistent distress, thoughts of self-harm, or anything that feels beyond a self-guided reflection, please reach out to a licensed clinician. The Therapist Directory in Brotherhood+ makes this easy, and the Crisis & Emergency Resources page has 24/7 helplines.'] },
      { heading: 'How we keep it safe', body: ['The AI Coach is configured with safety guardrails: crisis-language detection, mandatory referrals to professional support for high-risk topics, and a design that prioritises reflection over prescription.'] },
      { heading: 'Your privacy in AI chats', body: ['Coach conversations are stored so you can revisit them. They\'re never used to train third-party models. You can delete individual chats or your full history at any time.'] },
    ],
  },

  /* ---------------- 4. Crisis & Emergency Resources ---------------- */
  {
    id: 'crisis',
    title: 'Crisis & Emergency Resources',
    icon: 'LifeBuoy',
    summary: 'If you or someone you know is in immediate danger, please read this first.',
    sections: [
      {
        heading: 'Brotherhood+ is not a 24/7 monitored service',
        body: [
          'Brotherhood+ is a wellness and personal-growth tool. It is not monitored around the clock and cannot dispatch help. If you or someone you know is experiencing:',
          { list: [
            'Thoughts of suicide',
            'Thoughts of harming others',
            'Immediate danger',
            'A medical emergency',
          ]},
          'Please contact your local emergency services or one of the resources below right away.',
        ],
      },
      {
        heading: 'United States',
        body: [
          { list: [
            'Call 911 for any immediate emergency.',
            'Call or text 988 — Suicide & Crisis Lifeline (free, 24/7).',
            'Text HELLO to 741741 — Crisis Text Line (free, 24/7).',
            'Veterans: dial 988 then press 1.',
          ]},
        ],
      },
      {
        heading: 'International',
        body: [
          { list: [
            'United Kingdom: Samaritans — 116 123 (free, 24/7).',
            'Canada: Talk Suicide Canada — 1-833-456-4566.',
            'Australia: Lifeline — 13 11 14.',
            'For other countries, findahelpline.com maintains an up-to-date global directory.',
          ]},
        ],
      },
      { heading: 'What our AI Coach does in a crisis', body: ['If our AI Coach detects language suggesting a crisis, it will pause the conversation and direct you to this page. This is a safety measure — not a replacement for a real person on the line.'] },
    ],
  },

  /* ---------------- 5. Wellness Disclaimer ---------------- */
  {
    id: 'wellness',
    title: 'Wellness Disclaimer',
    icon: 'HeartPulse',
    summary: 'How to think about the scores, tools, and insights in Brotherhood+.',
    sections: [
      { heading: 'Designed for personal growth', body: ['Brotherhood+ is designed for education, wellness, personal growth, and self-reflection. Everything you see — scores, trends, recommended actions — exists to build self-awareness, not to label or diagnose.'] },
      {
        heading: 'The language we use',
        body: [
          'We deliberately avoid clinical language. Instead of "diagnosis" or "disorder", you\'ll see:',
          { list: [
            'Wellness Reflection',
            'Personal Wellness Snapshot',
            'Wellness Trends',
            'Growth Insights',
          ]},
        ],
      },
      { heading: 'What scores mean (and don\'t)', body: ['Scores are directional signals — a snapshot in time meant to support your self-awareness. They are NOT medical or psychological diagnoses. A high or low score does not mean you have a condition; it means today\'s reflection landed here.'] },
      { heading: 'Progress tracking', body: ['Progress views (streaks, wellness trends, achievements) are informational only. They are meant to celebrate consistency and help you notice patterns — not to compare you to anyone else or to a clinical standard.'] },
      { heading: 'When to seek a professional', body: ['If any pattern concerns you or if reflection alone isn\'t enough, please consult a licensed mental health professional. The Therapist Directory in Brotherhood+ is a good starting point.'] },
    ],
  },

  /* ---------------- 6. Therapist Directory Disclaimer ---------------- */
  {
    id: 'therapist',
    title: 'Therapist Directory Disclaimer',
    icon: 'Users',
    summary: 'Understanding how the directory works and what Brotherhood+\'s role is.',
    sections: [
      { heading: 'Independent professionals', body: ['Therapists listed in the Brotherhood+ directory are independent licensed professionals unless explicitly stated otherwise. They are not employed by Brotherhood+, and Brotherhood+ does not supervise their clinical work.'] },
      {
        heading: 'What Brotherhood+ does NOT do',
        body: [
          { list: [
            'We do NOT guarantee treatment outcomes.',
            'We do NOT supervise independent therapists\' clinical decisions.',
            'We are NOT responsible for the clinical care provided.',
            'We do NOT accept payments or referral fees from listed providers.',
          ]},
        ],
      },
      { heading: 'How we vet the directory', body: ['We verify licensure at listing and periodically thereafter. We give priority to clinicians with experience serving men of colour. We remove providers who violate ethical standards.'] },
      { heading: 'Your choice, your care', body: ['You are responsible for choosing the provider that best fits your needs. We recommend interviewing a therapist by phone or a first session before committing, and confirming their credentials with your state board.'] },
    ],
  },

  /* ---------------- 7. Subscription & Billing ---------------- */
  {
    id: 'billing',
    title: 'Subscription & Billing',
    icon: 'CreditCard',
    summary: 'Everything about plans, trials, renewals, and cancellations.',
    sections: [
      { heading: 'Plans', body: ['Brotherhood+ is available as a monthly plan or an annual plan. Annual plans offer a discount. Full pricing is shown in-app before you subscribe.'] },
      { heading: 'Free trial', body: ['New members can start with a free trial. You will not be charged until the trial ends. Cancel any time during the trial from Profile → Subscription and you\'ll never be billed.'] },
      { heading: 'Automatic renewal', body: ['Subscriptions renew automatically at the end of each billing cycle so your access is uninterrupted. You can turn off auto-renewal at any time from Profile → Subscription.'] },
      { heading: 'Cancellation', body: ['Cancel your subscription at any time. You\'ll keep access until the end of your current billing period. No cancellation fees, no phone calls required.'] },
      { heading: 'Refund policy', body: ['We offer refunds within 14 days of a first purchase if you have not made substantial use of premium features. Contact brotherhoodplusapp@gmail.com and we\'ll help. Renewal refunds are considered on a case-by-case basis.'] },
      { heading: 'Billing support', body: ['Questions about your subscription? Email brotherhoodplusapp@gmail.com or reach us from Profile → Support. We usually respond within one business day.'] },
    ],
  },

  /* ---------------- 8. Cookie & Analytics Policy ---------------- */
  {
    id: 'cookies',
    title: 'Cookie & Analytics Policy',
    icon: 'Cookie',
    summary: 'What we store on your device, and how to control it.',
    sections: [
      { heading: 'What we store', body: ['Necessary cookies keep you signed in and secure (authentication, session, security) and cannot be turned off without breaking the app. Functional cookies remember preferences like language and theme. We also use privacy-respecting, aggregated analytics — no third-party ad networks or trackers, and no profiling of individuals.'] },
      { heading: 'How to manage it', body: ['Clear Brotherhood+ cookies from your browser at any time; signing out clears your session. To opt out of analytics entirely, email brotherhoodplusapp@gmail.com.'] },
    ],
  },

  /* ---------------- 9. Community Guidelines ---------------- */
  {
    id: 'community',
    title: 'Community Guidelines',
    icon: 'HandshakeIcon',
    summary: 'How we protect the Brotherhood+ community.',
    sections: [
      {
        heading: 'The spirit of Brotherhood+',
        body: ['Brotherhood+ exists so men of colour can grow together — safely, honestly, and without judgement. Every interaction here should reflect that.'],
      },
      {
        heading: 'What we expect',
        body: [
          { list: [
            'Be respectful. Assume good intent. Disagree with the idea, not the person.',
            'No harassment, bullying, or targeted attacks.',
            'No hate speech, slurs, or discrimination.',
            'No illegal activity, including sharing of copyrighted material.',
            'No impersonation of others or misrepresentation of your identity.',
            'No exploitation — sexual, financial, or emotional.',
            'No spam, promotion, or off-topic advertising.',
          ]},
        ],
      },
      { heading: 'What happens if guidelines are broken', body: ['We may warn, restrict, or remove accounts that violate these standards. Serious violations (threats, illegal activity, harm to minors) are reported to the appropriate authorities.'] },
      { heading: 'Reporting a concern', body: ['If you see behaviour that breaks these guidelines, email brotherhoodplusapp@gmail.com. Reports are confidential and reviewed within 48 hours.'] },
    ],
  },
];

export function findPage(id) {
  return TRUST_SAFETY_PAGES.find(p => p.id === id) || TRUST_SAFETY_PAGES[0];
}
